import React, { useEffect, useRef, useState, useCallback } from 'react';
import jsQR from 'jsqr';
import { SwitchCamera, AlertCircle, Upload, CheckCircle2 } from 'lucide-react';

interface CameraQrScannerProps {
    onScan: (decodedText: string) => void;
    onError?: (error: string) => void;
    isActive: boolean;
}

const CameraQrScanner: React.FC<CameraQrScannerProps> = ({ onScan, onError, isActive }) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const animationFrameId = useRef<number | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const isScanningRef = useRef<boolean>(false);
    const hasFiredRef = useRef<boolean>(false);
    const cameraGeneration = useRef(0);

    const [cameraError, setCameraError] = useState<string | null>(null);
    const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
    const [loadingCamera, setLoadingCamera] = useState<boolean>(true);
    const [scannedSuccess, setScannedSuccess] = useState<boolean>(false);

    const stopCamera = useCallback(() => {
        ++cameraGeneration.current;
        isScanningRef.current = false;
        if (animationFrameId.current) {
            cancelAnimationFrame(animationFrameId.current);
            animationFrameId.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => {
                track.stop();
            });
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    }, []);

    const handleDetected = useCallback((data: string) => {
        if (!data || !data.trim()) return;
        if (hasFiredRef.current) return;
        hasFiredRef.current = true;
        isScanningRef.current = false;

        setScannedSuccess(true);
        stopCamera();
        
        if (navigator.vibrate) {
            navigator.vibrate(100);
        }
        onScan(data.trim());
    }, [onScan, stopCamera]);

    const scanQRCode = useCallback(async () => {
        if (!isScanningRef.current) return;
        const generation = cameraGeneration.current;

        const video = videoRef.current;
        const canvas = canvasRef.current;

        if (video && canvas && video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
            // Intentar primero con BarcodeDetector nativo si está disponible
            if ('BarcodeDetector' in window) {
                try {
                    const barcodeDetector = new (window as any).BarcodeDetector({ formats: ['qr_code'] });
                    const barcodes = await barcodeDetector.detect(video);
                    if (generation !== cameraGeneration.current || !isScanningRef.current) return;
                    if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
                        handleDetected(barcodes[0].rawValue);
                        return;
                    }
                } catch (e) {
                    // Fallback a jsQR
                }
            }

            // Fallback con jsQR
            const ctx = canvas.getContext('2d', { willReadFrequently: true });
            if (ctx) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const code = jsQR(imageData.data, imageData.width, imageData.height, {
                    inversionAttempts: 'attemptBoth',
                });

                if (code && code.data && code.data.trim()) {
                    handleDetected(code.data.trim());
                    return;
                }
            }
        }

        if (isScanningRef.current) {
            animationFrameId.current = requestAnimationFrame(scanQRCode);
        }
    }, [handleDetected]);

    const startCamera = useCallback(async () => {
        stopCamera();
        const generation = ++cameraGeneration.current;
        hasFiredRef.current = false;
        setLoadingCamera(true);
        setCameraError(null);
        setScannedSuccess(false);

        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error('Tu navegador no soporta acceso a la cámara.');
            }

            const constraints: MediaStreamConstraints = {
                video: {
                    facingMode: facingMode,
                    width: { ideal: 1280, min: 640 },
                    height: { ideal: 720, min: 480 },
                },
                audio: false,
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            if (generation !== cameraGeneration.current) {
                stream.getTracks().forEach(track => track.stop());
                return;
            }
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.setAttribute('playsinline', 'true');
                videoRef.current.onloadedmetadata = () => {
                    if (videoRef.current) {
                        const playPromise = videoRef.current.play();
                        if (playPromise !== undefined) {
                            playPromise
                                .then(() => {
                                    setLoadingCamera(false);
                                    isScanningRef.current = true;
                                    animationFrameId.current = requestAnimationFrame(scanQRCode);
                                })
                                .catch((err) => {
                                    if (err.name !== 'AbortError') {
                                        /* Browser diagnostics omitted. */
                                    }
                                });
                        }
                    }
                };
            }
        } catch (err: any) {
            if (generation !== cameraGeneration.current) return;
            /* Browser diagnostics omitted. */
            setLoadingCamera(false);
            const msg =
                err.name === 'NotAllowedError'
                    ? 'Permiso de cámara denegado. Permite el acceso a la cámara en la barra del navegador.'
                    : err.message || 'No se pudo iniciar la cámara.';
            setCameraError(msg);
            if (onError) onError(msg);
        }
    }, [facingMode, onError, scanQRCode, stopCamera]);

    useEffect(() => {
        if (isActive) {
            startCamera();
        } else {
            stopCamera();
        }

        return () => {
            stopCamera();
        };
    }, [isActive, startCamera, stopCamera]);

    // Procesar imagen subida (por si prefiere subir archivo / captura)
    const handleFileScan = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const generation = cameraGeneration.current;
        const reader = new FileReader();
        reader.onload = (event) => {
            if (generation !== cameraGeneration.current) return;
            const img = new Image();
            img.onload = () => {
                if (generation !== cameraGeneration.current) return;
                const canvas = canvasRef.current || document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(img, 0, 0);
                    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                    const code = jsQR(imageData.data, imageData.width, imageData.height, {
                        inversionAttempts: 'attemptBoth',
                    });
                    if (code && code.data) {
                        handleDetected(code.data);
                    } else {
                        if (onError) onError('No se detectó ningún código QR en la imagen seleccionada.');
                    }
                }
            };
            img.src = event.target?.result as string;
        };
        reader.readAsDataURL(file);
    };

    const toggleFacingMode = () => {
        setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
    };

    return (
        <div className="space-y-3">
            <div className="relative w-full rounded-2xl overflow-hidden bg-black aspect-square max-h-[300px] flex items-center justify-center border-2 border-slate-700 shadow-inner">
                <video
                    ref={videoRef}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                />
                <canvas ref={canvasRef} className="hidden" />

                {/* Guía visual de escaneo */}
                {!loadingCamera && !cameraError && !scannedSuccess && (
                    <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center p-6">
                        <div className="relative w-44 h-44 border-2 border-[#8b3fa8] rounded-2xl shadow-[0_0_25px_rgba(139, 63, 168,0.5)] animate-pulse">
                            <div className="absolute top-0 left-0 w-5 h-5 border-t-4 border-l-4 border-white -mt-1 -ml-1 rounded-tl" />
                            <div className="absolute top-0 right-0 w-5 h-5 border-t-4 border-r-4 border-white -mt-1 -mr-1 rounded-tr" />
                            <div className="absolute bottom-0 left-0 w-5 h-5 border-b-4 border-l-4 border-white -mb-1 -ml-1 rounded-bl" />
                            <div className="absolute bottom-0 right-0 w-5 h-5 border-b-4 border-r-4 border-white -mb-1 -mr-1 rounded-br" />
                        </div>
                        <span className="mt-3 px-3 py-1 bg-black/70 backdrop-blur-sm text-[11px] text-white rounded-full font-medium">
                            Apunta al código QR del cliente
                        </span>
                    </div>
                )}

                {/* Feedback de escaneo exitoso */}
                {scannedSuccess && (
                    <div className="absolute inset-0 bg-emerald-950/80 backdrop-blur-sm flex flex-col items-center justify-center text-white gap-2 animate-fade-in">
                        <CheckCircle2 className="w-12 h-12 text-emerald-400 animate-bounce" />
                        <span className="text-sm font-bold text-emerald-200">¡Código QR detectado!</span>
                    </div>
                )}

                {/* Spinner de carga */}
                {loadingCamera && (
                    <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center text-white gap-2">
                        <div className="w-8 h-8 border-2 border-white/20 border-t-[#8b3fa8] rounded-full animate-spin" />
                        <span className="text-xs text-gray-300">Iniciando cámara...</span>
                    </div>
                )}

                {/* Mensaje de error */}
                {cameraError && (
                    <div className="absolute inset-0 bg-black/90 p-4 flex flex-col items-center justify-center text-center text-red-400 gap-2">
                        <AlertCircle className="w-8 h-8" />
                        <p className="text-xs">{cameraError}</p>
                        <button
                            type="button"
                            onClick={startCamera}
                            className="mt-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-xs font-semibold"
                        >
                            Reintentar Cámara
                        </button>
                    </div>
                )}

                {/* Botón para cambiar de cámara (trasera / frontal) */}
                {!loadingCamera && !cameraError && (
                    <button
                        type="button"
                        onClick={toggleFacingMode}
                        className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black text-white rounded-full backdrop-blur-sm transition-colors"
                        title="Cambiar cámara"
                    >
                        <SwitchCamera className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Opción alternativa: Subir imagen con QR */}
            <div className="flex items-center justify-center">
                <input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleFileScan}
                    className="hidden"
                />
                <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="text-xs text-[#61257b] hover:text-blue-700 font-semibold flex items-center gap-1.5 py-1 px-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                >
                    <Upload className="w-3.5 h-3.5" />
                    ¿Tienes una captura o foto del QR? Subir imagen
                </button>
            </div>
        </div>
    );
};

export default CameraQrScanner;

